import { useCallback, useEffect, useState } from "react";

import { briefingApiSlice, type BriefingGenerationStatus } from "api/briefingApi.slice";
import { ErrorMessage, Link, ProgressBar } from "common/components";
import { logger } from "common/utilities/logger";
import { useDispatch } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { briefingGenerationSlice } from "domains/briefing/briefingGeneration.slice";
import * as Styled from "domains/briefing/components/BriefingListItem/BriefingListItem.styled";
import { CompletedGenerationActionButtons } from "domains/briefing/components/CompletedGenerationActionButtons/CompletedGenerationActionButtons";
import { CompletedGenerationMessage } from "domains/briefing/components/CompletedGenerationMessage/CompletedGenerationMessage";
import { interpolate } from "domains/language/interpolate";
const getGenerationProgressText = (progress: number, loadingGenerationSentences: string[]) => {
  switch (true) {
    case progress < 25:
      return loadingGenerationSentences[0];
    case progress < 50:
      return loadingGenerationSentences[1];
    case progress < 75:
      return loadingGenerationSentences[2];
    case progress < 100:
      return loadingGenerationSentences[3];
    default:
      return loadingGenerationSentences[loadingGenerationSentences.length - 1];
  }
};

type BriefingListItemProps = {
  topicName: string;
  progress: number;
  status: BriefingGenerationStatus;
  briefingId: string;
};

export const BriefingListItem = ({ topicName, progress, status, briefingId }: BriefingListItemProps) => {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const [showDownloadError, setShowDownloadError] = useState(false);

  const { updateBriefingJobProgress, updateBriefingJobStatus, updateBriefingJobDownloadUrl, removeBriefing } =
    briefingGenerationSlice.actions;

  const { data: briefingGenerationJobStatus } = briefingApiSlice.endpoints.monitorBriefingGeneration.useQuery(
    {
      jobId: briefingId,
    },
    { skip: !briefingId || (progress === 100 && status === "Done") || status === "Error", pollingInterval: 3000 },
  );

  const { data: generatedBriefing } = briefingApiSlice.endpoints.fetchGeneratedBriefing.useQuery(
    {
      briefingId,
    },
    { skip: !briefingId || progress !== 100 || status !== "Done" },
  );

  useEffect(() => {
    dispatch(
      updateBriefingJobProgress({ id: briefingId, progress: (briefingGenerationJobStatus?.progress ?? 0) * 100 }),
    );

    if (briefingGenerationJobStatus?.status)
      dispatch(updateBriefingJobStatus({ id: briefingId, status: briefingGenerationJobStatus.status }));

    if (generatedBriefing?.live_briefing_url)
      dispatch(updateBriefingJobDownloadUrl({ id: briefingId, downloadUrl: generatedBriefing.live_briefing_url }));
  }, [
    briefingGenerationJobStatus?.progress,
    briefingGenerationJobStatus?.status,
    briefingId,
    dispatch,
    generatedBriefing?.live_briefing_url,
    progress,
    status,
    updateBriefingJobDownloadUrl,
    updateBriefingJobProgress,
    updateBriefingJobStatus,
  ]);

  const handleDownload = useCallback(() => {
    setShowDownloadError(false);
    if (!generatedBriefing?.live_briefing_url) return;

    fetch(generatedBriefing?.live_briefing_url)
      .then((response) => response.blob())
      .then((blob) => {
        const pdfBlob = new Blob([blob], { type: "application/pdf" });

        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${topicName || "downloaded-file"}.pdf`;
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
      })
      .catch((error: unknown) => {
        logger.error("[BriefingListItem] Error fetching the file", error);
        setShowDownloadError(true);
      });
  }, [generatedBriefing?.live_briefing_url, topicName]);

  const generationProgressText = [
    translate("app.briefing.progress_collecting_data"),
    translate("app.briefing.progress_preparing_data"),
    translate("app.briefing.progress_assembling_PDF"),
    translate("app.briefing.progress_finalising_PDF"),
  ];

  const onRemoveClick = () => dispatch(removeBriefing(briefingId));
  const supportEmail = () => <Link href="mailto:intelligence@weforum.org">intelligence@weforum.org</Link>;

  return (
    <Styled.ItemBox status={status}>
      <Styled.Row>
        <div>
          <Styled.BriefingTitle>{topicName}</Styled.BriefingTitle>
          <CompletedGenerationMessage status={status} />
        </div>
        <CompletedGenerationActionButtons
          status={status}
          onDownloadButtonPress={handleDownload}
          onCrossButtonPress={onRemoveClick}
        />
      </Styled.Row>
      {status === "Processing" && (
        <ProgressBar percentage={progress} text={getGenerationProgressText(progress, generationProgressText)} />
      )}
      <ErrorMessage isActive={showDownloadError}>
        {interpolate(translate("app.topic.briefing.error_message"), { supportEmail })}
      </ErrorMessage>
    </Styled.ItemBox>
  );
};
